import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchPaymentTypes,
  createPaymentType,
  updatePaymentType,
  deletePaymentType,
} from '../../api/paymentTypes';

export default function PaymentTypesManager({ authConfig, onUnauthorized }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchPaymentTypes(authConfig);
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to load payment types.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => {
    setEditingId(null);
    setShowAddModal(false);
    setName('');
    setCode('');
  };

  const startEdit = (item) => {
    setEditingId(item._id || item.id);
    setShowAddModal(false);
    setName(item.name || '');
    setCode(item.code || '');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = { name: name.trim(), code: code.trim() || undefined };
      if (editingId) {
        await updatePaymentType(editingId, payload, authConfig);
      } else {
        await createPaymentType(payload, authConfig);
      }
      startAdd();
      await load();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment type?')) return;
    try {
      await deletePaymentType(id, authConfig);
      await load();
      if (editingId === id) startAdd();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to delete.');
    }
  };

  const openAdd = () => {
    startAdd();
    setShowAddModal(true);
  };

  const renderForm = ({ submitLabel }) => (
    <form onSubmit={handleSave} className="space-y-3 p-5">
      <input
        type="text"
        placeholder="Name (e.g. Cash, Card)"
        className="border p-2 rounded text-sm w-full"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <input
        type="text"
        placeholder="Code (optional)"
        className="border p-2 rounded text-sm w-full"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded text-sm disabled:opacity-60 hover:bg-indigo-500">
          {saving ? 'Saving...' : submitLabel}
        </button>
        <button type="button" onClick={startAdd} className="flex-1 px-4 py-2 border rounded text-sm hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </form>
  );

  const renderActions = (item) => (
    <div className="flex gap-2">
      <button type="button" onClick={() => startEdit(item)} className="flex-1 text-sm border px-3 py-2 rounded hover:bg-gray-50">Edit</button>
      <button type="button" onClick={() => handleDelete(item._id || item.id)} className="flex-1 text-sm bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700">Delete</button>
    </div>
  );

  if (loading) return <p className="text-gray-600">Loading payment types...</p>;

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-medium">Payment types</h4>
          <p className="text-xs text-gray-500">Manage POS payment options.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={openAdd} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"><span className="text-lg leading-none">+</span>Add</button>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
            <button type="button" onClick={() => setViewMode('list')} className={`rounded-md px-3 py-1.5 ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>List</button>
            <button type="button" onClick={() => setViewMode('grid')} className={`rounded-md px-3 py-1.5 ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>Grid</button>
          </div>
        </div>
      </div>
      {list.length === 0 ? (
        <div className="rounded border p-3 text-sm text-gray-500">No payment types yet.</div>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {list.map((item) => (
            <div key={item._id || item.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="truncate font-semibold text-gray-900">{item.name}</p>
              <p className="mb-4 text-sm text-gray-500">{item.code || 'No code'}</p>
              {renderActions(item)}
            </div>
          ))}
        </div>
      ) : (
        <ul className="border rounded divide-y">
          {list.map((item) => (
            <li key={item._id || item.id} className="p-3 flex items-center justify-between gap-3">
              <span className="text-sm">{item.name}{item.code ? ` (${item.code})` : ''}</span>
              <div className="w-44 shrink-0">{renderActions(item)}</div>
            </li>
          ))}
        </ul>
      )}

      {showAddModal && !editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="add-payment-type-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="add-payment-type-title" className="font-semibold text-gray-900">Add payment type</h4>
              <button type="button" onClick={startAdd} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close add payment type">×</button>
            </div>
            {renderForm({ submitLabel: 'Add payment type' })}
          </div>
        </div>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="edit-payment-type-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="edit-payment-type-title" className="font-semibold text-gray-900">Edit payment type</h4>
              <button type="button" onClick={startAdd} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close edit payment type">×</button>
            </div>
            {renderForm({ submitLabel: 'Update payment type' })}
          </div>
        </div>
      )}
    </div>
  );
}
